/**************************************************************
 * usnewsSimulatorSupabase.ts
 *
 * Example script to:
 *   1) Fetch all rows from Supabase table "US News University Rankings".
 *   2) Compute raw simulated scores using your final means/stds.
 *   3) Apply a univariate cubic spline to calibrate scores.
 *   4) Log some results.
 *
 * NOTE: "percent_full_time_faculty_3" is REMOVED from the calculation.
 **************************************************************/

import CubicSpline from "cubic-spline";
import { MetricsState, SubMetric } from '@/types/metrics';
import { fetchUniversityRankings } from '@/services/universityService';

/** ------------- Types for metrics and calculations ------------- */
interface UniversityMetrics {
  grad_rate: number;
  retention_rate: number;
  pell_grad_rate: number;
  non_pell_grad_rate: number;
  overperformance_underperformance: number;
  median_debt: number;
  grads_earning_more: number;
  peer_assessment: number;
  faculty_salaries_rank: number;
  percent_full_time_faculty_2: number;
  student_faculty_ratio: number;
  financial_resources_rank: number;
  sat_act_25th_75th: number;
  bibliometric_rank: number;
}

/** ------------- Constants for the calculator ------------- */
export const METRIC_WEIGHTS = {
  grad_rate: 0.16,                      // 16%
  retention_rate: 0.05,                 // 5%
  pell_grad_rate: 0.055,                // 5.5%
  non_pell_grad_rate: 0.055,            // 5.5%
  overperformance_underperformance: 0.10,// 10%
  median_debt: 0.05,                    // 5%
  grads_earning_more: 0.05,             // 5%
  peer_assessment: 0.20,                // 20%
  faculty_salaries_rank: 0.06,          // 6%
  percent_full_time_faculty_2: 0.02,    // 2%
  student_faculty_ratio: 0.03,          // 3%
  financial_resources_rank: 0.08,       // 8%
  sat_act_25th_75th: 0.05,              // 5%
  bibliometric_rank: 0.04               // 4%
};

/**
 * Columns treated as "rank-based" — in these, smaller numeric values mean better rank
 * (e.g., a rank of 1 is the best and 432 is the worst).
 */
export const RANK_COLUMNS = new Set([
  "faculty_salaries_rank",
  "financial_resources_rank",
  "bibliometric_rank"
]);

/**
 * Columns where a *smaller* raw value is *better*, but they are not ranks per se.
 * For example, in median_debt, a lower absolute value is better.
 */
export const NEGATIVE_COLUMNS = new Set([
  "median_debt",
  "student_faculty_ratio"
]);

/**
 * Columns where the raw value is a percentage (e.g., 0.72 => 72%).
 * We may need to convert values >1 to a 0-1 scale by dividing by 100.
 */
export const PERCENTAGE_FIELDS = new Set([
  "grad_rate",
  "retention_rate",
  "pell_grad_rate",
  "non_pell_grad_rate",
  "grads_earning_more",
  "percent_full_time_faculty_2"
]);

/** ------------- Normalization params (removing pct_ft_faculty_3) ------------- */
interface NormParam { mean: number; std: number; max?: number; }
const NORM_PARAMS: Record<string, NormParam> = {
  grad_rate:                  { mean: 0.6560,   std: 0.1626 },
  retention_rate:             { mean: 0.8109,   std: 0.0982 },
  pell_grad_rate:             { mean: 0.5943,   std: 0.1738 },
  non_pell_grad_rate:         { mean: 0.6871,   std: 0.1517 },
  overperformance_underperformance: { mean: -0.0779, std: 6.5047 },
  median_debt:                { mean: 21534.4621, std: 3906.9231 },
  grads_earning_more:         { mean: 0.8585,   std: 0.0560 },
  peer_assessment:            { mean: 2.7024,   std: 0.7536 },
  faculty_salaries_rank:      { mean: 220.5061, std: 126.1623, max: 432 },
  percent_full_time_faculty_2:{ mean: 0.7983,   std: 0.1449 },
  student_faculty_ratio:      { mean: 14.82,    std: 4.4619 },
  financial_resources_rank:   { mean: 217.7445, std: 126.7055, max: 432 },
  sat_act_25th_75th:          { mean: 25.5218,  std: 4.9724 },
  bibliometric_rank:          { mean: 215.5815, std: 125.2202, max: 428 }
};

/** ------------- Score ranges for realistic calibration ------------- */
const SCORE_RANGES = [
  { min: -2.0, max: -0.4, baseScore: 30, maxScore: 45 },    // Tier 1 
  { min: -0.4, max: -0.2, baseScore: 45, maxScore: 50 },    // Tier 2
  { min: -0.2, max: 0.0, baseScore: 50, maxScore: 52.5 },   // Tier 3
  { min: 0.0, max: 0.1, baseScore: 52.5, maxScore: 54 },    // Tier 4
  { min: 0.1, max: 0.19, baseScore: 54, maxScore: 55 },     // Tier 5
  { min: 0.19, max: 0.209, baseScore: 55, maxScore: 55 },   // Tier 6 (DePaul base: 55)
  { min: 0.209, max: 0.22, baseScore: 55, maxScore: 55.5 }, // Tier 7
  { min: 0.22, max: 0.25, baseScore: 55.5, maxScore: 56 },  // Tier 8
  { min: 0.25, max: 0.3, baseScore: 56, maxScore: 57 },     // Tier 9
  { min: 0.3, max: 0.35, baseScore: 57, maxScore: 58 },     // Tier 10
  { min: 0.35, max: 0.4, baseScore: 58, maxScore: 59 },     // Tier 11
  { min: 0.4, max: 0.6, baseScore: 59, maxScore: 65 },      // Tier 12
  { min: 0.6, max: 1.0, baseScore: 65, maxScore: 75 },      // Tier 13
  { min: 1.0, max: 2.0, baseScore: 75, maxScore: 100 }      // Tier 14
];

/** ------------- Spline Calibration Data ------------- */
const rawSimScores = [
  -2.0,   // Very poor performance
  -1.0,   // Poor performance
  -0.5,   // Below average
  -0.25,  // Slightly below average
  0.0,    // Average
  0.15,   // Below DePaul
  0.16,   // Just below DePaul
  0.171891, // DePaul's exact base raw score
  0.175,  // Tiny improvement
  0.18,   // Small improvement
  0.185,  // Minor improvement
  0.19,   // Modest improvement
  0.195,  // Notable improvement
  0.20,   // Significant improvement
  0.21,   // Strong improvement
  0.22,   // Very strong improvement
  0.25,   // Excellent improvement
  0.30,   // Outstanding improvement
  0.35,   // Exceptional improvement
  0.40,   // Elite improvement
  1.0     // World-class performance
];

const officialScores = [
  30,     // Very poor performance
  40,     // Poor performance
  45,     // Below average
  50,     // Slightly below average
  52.5,   // Average
  54.5,   // Below DePaul
  54.8,   // Just below DePaul
  55.0,   // DePaul's exact base score
  55.5,   // Tiny improvement (increased from 55.05)
  56.0,   // Small improvement (increased from 55.1)
  56.5,   // Minor improvement (increased from 55.15)
  57.0,   // Modest improvement (increased from 55.2)
  57.5,   // Notable improvement (increased from 55.25)
  58.0,   // Significant improvement (increased from 55.3)
  58.5,   // Strong improvement (increased from 55.4)
  59.0,   // Very strong improvement (increased from 55.5)
  60.0,   // Excellent improvement (increased from 55.7)
  61.5,   // Outstanding improvement (increased from 56.0)
  63.0,   // Exceptional improvement (increased from 56.5)
  65.0,   // Elite improvement (increased from 57.0)
  75.0    // World-class performance (increased from 65.0)
];

// --- NEW Global variable for optimized mapping ---
let optimizedMapping: number[] | null = null;

// At the top of the file (after imports), add:
const CLIP_THRESHOLD = 3; // You can adjust this value to trade-off between error reduction and correlation preservation

/*
 * New helper function implementing PCHIP interpolation.
 * Given arrays x and y (of equal length, with x strictly increasing) and a target x value,
 * it returns the interpolated y value using piecewise cubic Hermite interpolation that preserves monotonicity.
 */
function pchipInterpolate(x: number[], y: number[], x_target: number): number {
  const n = x.length;
  // Clamp x_target to the domain
  if (x_target <= x[0]) return y[0];
  if (x_target >= x[n - 1]) return y[n - 1];

  // Step 1: find the interval x[i] <= x_target <= x[i+1]
  let i = 0;
  for (let j = 0; j < n - 1; j++) {
    if (x_target >= x[j] && x_target <= x[j + 1]) {
      i = j;
      break;
    }
  }

  const h = x[i + 1] - x[i];
  const t = (x_target - x[i]) / h;

  // Step 2: compute slopes (delta) for each interval
  const delta: number[] = new Array(n - 1);
  for (let j = 0; j < n - 1; j++) {
    delta[j] = (y[j + 1] - y[j]) / (x[j + 1] - x[j]);
  }

  // Step 3: compute derivatives m at each knot using Fritsch-Butland method
  const m: number[] = new Array(n);
  m[0] = delta[0];
  m[n - 1] = delta[n - 2];
  for (let j = 1; j < n - 1; j++) {
    if (delta[j - 1] * delta[j] <= 0) {
      m[j] = 0;
    } else {
      const w1 = 2 * (x[j + 1] - x[j]) + (x[j] - x[j - 1]);
      const w2 = (x[j + 1] - x[j]) + 2 * (x[j] - x[j - 1]);
      m[j] = (w1 + w2) / ((w1 / delta[j - 1]) + (w2 / delta[j]));
    }
  }

  // Step 4: Compute Hermite basis functions
  const h00 = 2 * t * t * t - 3 * t * t + 1;
  const h10 = t * t * t - 2 * t * t + t;
  const h01 = -2 * t * t * t + 3 * t * t;
  const h11 = t * t * t - t * t;

  return h00 * y[i] + h10 * h * m[i] + h01 * y[i + 1] + h11 * h * m[i + 1];
}

// Updated calibration function using PCHIP interpolation
function calibrateScore(rawScore: number): number {
  // Define the knot points for raw scores and their corresponding official scores
  const rawSimScores = [
    -2.0,
    -1.0,
    -0.5,
    -0.25,
    0.0,
    0.15,
    0.16,
    0.171891,
    0.175,
    0.18,
    0.185,
    0.19,
    0.195,
    0.20,
    0.21,
    0.22,
    0.25,
    0.30,
    0.35,
    0.40,
    1.0
  ];
  const officialScores = [
    30,
    40,
    45,
    50,
    52.5,
    54.5,
    54.8,
    55.0,
    55.5,   // updated
    56.0,   // updated
    56.5,   // updated
    57.0,   // updated
    57.5,   // updated
    58.0,   // updated
    58.5,   // updated
    59.0,   // updated
    60.0,   // updated
    61.5,   // updated
    63.0,   // updated
    65.0,   // updated
    75.0    // updated
  ];

  // Clamp rawScore within the defined range
  const minRaw = rawSimScores[0];
  const maxRaw = rawSimScores[rawSimScores.length - 1];
  const clampedRaw = Math.max(minRaw, Math.min(maxRaw, rawScore));

  // Use PCHIP interpolation to get the calibrated score
  let calibrated = pchipInterpolate(rawSimScores, officialScores, clampedRaw);

  // Round to 1 decimal place and ensure it's between 0 and 100
  calibrated = Math.round(calibrated * 10) / 10;
  return Math.min(100, Math.max(0, calibrated));
}

// Helper function to find metric by database field
const findMetricByDbField = (metrics: MetricsState, dbField: string): SubMetric | null => {
  for (const category of Object.values(metrics)) {
    const metric = category.find(m => m.dbField === dbField);
    if (metric) return metric;
  }
  return null;
};

// Convert MetricsState to UniversityMetrics
const getUniversityMetrics = (metrics: MetricsState): UniversityMetrics => {
  const getMetricValue = (dbField: string): number => {
    const metric = findMetricByDbField(metrics, dbField);
    return metric ? metric.value : 0;
  };

  return {
    grad_rate: getMetricValue('grad_rate'),
    retention_rate: getMetricValue('retention_rate'),
    pell_grad_rate: getMetricValue('pell_grad_rate'),
    non_pell_grad_rate: getMetricValue('non_pell_grad_rate'),
    overperformance_underperformance: getMetricValue('overperformance_underperformance'),
    median_debt: getMetricValue('median_debt'),
    grads_earning_more: getMetricValue('grads_earning_more'),
    peer_assessment: getMetricValue('peer_assessment'),
    faculty_salaries_rank: getMetricValue('faculty_salaries_rank'),
    percent_full_time_faculty_2: getMetricValue('percent_full_time_faculty_2'),
    student_faculty_ratio: getMetricValue('student_faculty_ratio'),
    financial_resources_rank: getMetricValue('financial_resources_rank'),
    sat_act_25th_75th: getMetricValue('sat_act_25th_75th'),
    bibliometric_rank: getMetricValue('bibliometric_rank')
  };
};

/** ------------- Dynamic Dataset Statistics ------------- */
interface DatasetStats {
  mean: number;
  std: number;
  min: number;
  max: number;
}

interface UniversityDataset {
  universities: Array<UniversityMetrics>;
  stats: Record<keyof UniversityMetrics, DatasetStats>;
}

// Calculate statistics for a numeric array
function calculateStats(values: number[]): DatasetStats {
  const filteredValues = values.filter(v => v !== null && !isNaN(v));
  const n = filteredValues.length;
  
  if (n === 0) {
    return { mean: 0, std: 1, min: 0, max: 0 };
  }
  
  const mean = filteredValues.reduce((a, b) => a + b, 0) / n;
  const variance = filteredValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
  const std = Math.sqrt(variance);
  
  return {
    mean,
    std: std || 1, // Avoid division by zero
    min: Math.min(...filteredValues),
    max: Math.max(...filteredValues)
  };
}

// Compute dataset statistics in real-time
function computeDatasetStats(universities: UniversityMetrics[]): Record<keyof UniversityMetrics, DatasetStats> {
  const stats: Partial<Record<keyof UniversityMetrics, DatasetStats>> = {};
  
  // Get all metric names from the first university
  const metricNames = Object.keys(universities[0]) as Array<keyof UniversityMetrics>;
  
  // Calculate stats for each metric
  for (const metric of metricNames) {
    const values = universities.map(u => u[metric]);
    stats[metric] = calculateStats(values);
  }
  
  return stats as Record<keyof UniversityMetrics, DatasetStats>;
}

// Types for validation
interface ValidationCase {
  metrics: MetricsState;
  score: number;
  university: string;
}

interface ValidationResult {
  university: string;
  predicted: number;
  actual: number;
  error: number;
}

interface ValidationSummary {
  correlation: number;
  meanAbsoluteError: number;
  medianAbsoluteError: number;
  rootMeanSquaredError: number;
  spearmanCorrelation: number;
  outliers: ValidationResult[];
}

// Main simulation function that computes the score
export const calculateSimulatedScore = async (metrics: MetricsState, allUniversities?: UniversityMetrics[], suppressLogs: boolean = false): Promise<number> => {
  const universityMetrics = getUniversityMetrics(metrics);
  let rawScore = 0;

  // If allUniversities is not provided, fetch them
  if (!allUniversities) {
    allUniversities = await fetchAllUniversities();
  }

  // Calculate real-time statistics for all metrics
  const realTimeStats: Record<string, { mean: number; std: number; max?: number }> = {};
  
  for (const metric in METRIC_WEIGHTS) {
    const values = allUniversities.map(u => Number(u[metric]) || 0);
    const filteredValues = values.filter(v => !isNaN(v));
    const mean = filteredValues.reduce((a, b) => a + b, 0) / filteredValues.length;
    const variance = filteredValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / filteredValues.length;
    const std = Math.sqrt(variance) || 1;
    const max = RANK_COLUMNS.has(metric) ? Math.max(...filteredValues) : undefined;
    realTimeStats[metric] = { mean, std, max };
  }

  const BASELINE_METRICS: UniversityMetrics = {
    grad_rate: 0.70,
    retention_rate: 0.85,
    pell_grad_rate: 0.64,
    non_pell_grad_rate: 0.73,
    overperformance_underperformance: 4,
    median_debt: 23168,
    grads_earning_more: 0.85,
    peer_assessment: 2.9,
    faculty_salaries_rank: 195,
    percent_full_time_faculty_2: 0.751,
    student_faculty_ratio: 17,
    financial_resources_rank: 263,
    sat_act_25th_75th: 26,
    bibliometric_rank: 226
  };

  let baselineScore = 0;

  // Check if input matches baseline
  const isBaseline = Object.keys(BASELINE_METRICS).every(metric => {
    const baselineVal = BASELINE_METRICS[metric as keyof typeof BASELINE_METRICS];
    const inputVal = universityMetrics[metric];
    return Math.abs(inputVal - baselineVal) / (Math.abs(baselineVal) || 1) < 0.01;
  });

  if (isBaseline) {
    if (!suppressLogs) console.log('Input metrics match baseline metrics. Returning calibrated score of 55.');
    return 55;
  }

  // Compute rawScore from input metrics
  for (const metric in METRIC_WEIGHTS) {
    const value = Number(universityMetrics[metric]);
    if (isNaN(value)) continue;

    let normalizedValue = value;
    if (PERCENTAGE_FIELDS.has(metric)) {
      normalizedValue = value > 1 ? value / 100.0 : value;
    }

    let z = 0;
    const stats = realTimeStats[metric];

    if (stats && stats.std !== 0) {
      // If it's a rank column or negative column, invert the sign so bigger values are penalized
      if (RANK_COLUMNS.has(metric) || NEGATIVE_COLUMNS.has(metric)) {
        z = -(normalizedValue - stats.mean) / stats.std;
      } else {
        z = (normalizedValue - stats.mean) / stats.std;
      }
      // Clip z-score to the range [-3, 3] to reduce the impact of outliers
      z = Math.max(Math.min(z, CLIP_THRESHOLD), -CLIP_THRESHOLD);
      const contribution = METRIC_WEIGHTS[metric] * z;
      rawScore += contribution;
      if (!suppressLogs) {
        console.log(`Metric ${metric}:`, {
          originalValue: value,
          normalizedValue,
          zScore: z,
          weight: METRIC_WEIGHTS[metric],
          contribution
        });
      }
    }
  }

  // Separately, compute baselineScore using hardcoded BASELINE_METRICS
  for (const metric in METRIC_WEIGHTS) {
    let baselineValue = BASELINE_METRICS[metric as keyof UniversityMetrics];
    if (PERCENTAGE_FIELDS.has(metric)) {
      baselineValue = baselineValue > 1 ? baselineValue / 100.0 : baselineValue;
    }

    let baselineZ = 0;
    const stats = realTimeStats[metric];
    if (stats && stats.std !== 0) {
      if (RANK_COLUMNS.has(metric) || NEGATIVE_COLUMNS.has(metric)) {
        baselineZ = -(baselineValue - stats.mean) / stats.std;
      } else {
        baselineZ = (baselineValue - stats.mean) / stats.std;
      }
      baselineScore += METRIC_WEIGHTS[metric] * baselineZ;
    }
  }

  // Center the raw score around DePaul's baseline
  const centeredRawScore = rawScore - baselineScore + 0.171891;
  if (!suppressLogs) console.log('Final raw score:', centeredRawScore);
  let finalScore;
  if (optimizedMapping) {
    finalScore = calibrateScoreCustom(centeredRawScore, optimizedMapping);
  } else {
    finalScore = calibrateScore(centeredRawScore);
  }
  if (!suppressLogs) console.log('Calibrated score:', finalScore);

  return finalScore;
};

// Implement fetchAllUniversities to use Supabase data
async function fetchAllUniversities(): Promise<UniversityMetrics[]> {
  try {
    const rankings = await fetchUniversityRankings();
    
    // Convert the rankings data to UniversityMetrics format
    return rankings.map(uni => ({
      grad_rate: Number(uni.grad_rate) || 0,
      retention_rate: Number(uni.retention_rate) || 0,
      pell_grad_rate: Number(uni.pell_grad_rate) || 0,
      non_pell_grad_rate: Number(uni.non_pell_grad_rate) || 0,
      overperformance_underperformance: Number(uni.overperformance_underperformance) || 0,
      median_debt: Number(uni.median_debt) || 0,
      grads_earning_more: Number(uni.grads_earning_more) || 0,
      peer_assessment: Number(uni.peer_assessment) || 0,
      faculty_salaries_rank: Number(uni.faculty_salaries_rank) || 0,
      percent_full_time_faculty_2: Number(uni.percent_full_time_faculty_2) || 0,
      student_faculty_ratio: Number(uni.student_faculty_ratio) || 0,
      financial_resources_rank: Number(uni.financial_resources_rank) || 0,
      sat_act_25th_75th: Number(uni.sat_act_25th_75th) || 0,
      bibliometric_rank: Number(uni.bibliometric_rank) || 0
    }));
  } catch (error) {
    console.error('Error fetching university data:', error);
    throw new Error('Failed to fetch university data from Supabase');
  }
}

// Function to determine new rank based on score
export const calculateNewRank = (
  currentScore: number,
  universities: Array<{ rank: number; score: number }>
): number => {
  const sortedUniversities = [...universities].sort((a, b) => b.score - a.score);
  
  for (let i = 0; i < sortedUniversities.length; i++) {
    if (currentScore >= sortedUniversities[i].score) {
      return sortedUniversities[i].rank;
    }
  }
  
  return sortedUniversities[sortedUniversities.length - 1].rank + 1;
};

/** ------------- Type Conversion Functions ------------- */
const convertToMetricsState = (metrics: UniversityMetrics): MetricsState => {
  return {
    outcomes: [
      {
        label: "Graduation Rate",
        value: metrics.grad_rate,
        min: 19,
        max: 98,
        weight: "16%",
        dbField: "grad_rate",
        description: "Six-year graduation rate",
        bigger_is_better: true,
        isPercentage: true
      },
      {
        label: "Retention Rate",
        value: metrics.retention_rate,
        min: 46,
        max: 99,
        weight: "5%",
        dbField: "retention_rate",
        description: "First-year retention rate",
        bigger_is_better: true,
        isPercentage: true
      },
      {
        label: "Pell Grant Graduation Rate",
        value: metrics.pell_grad_rate,
        min: 13,
        max: 97,
        weight: "5.5%",
        dbField: "pell_grad_rate",
        description: "Graduation rate for Pell Grant recipients",
        bigger_is_better: true,
        isPercentage: true
      },
      {
        label: "Non-Pell Grant Graduation Rate",
        value: metrics.non_pell_grad_rate,
        min: 16,
        max: 98,
        weight: "5.5%",
        dbField: "non_pell_grad_rate",
        description: "Graduation rate for non-Pell Grant recipients",
        bigger_is_better: true,
        isPercentage: true
      }
    ],
    facultyResources: [
      {
        label: "Faculty Salaries Rank",
        value: metrics.faculty_salaries_rank,
        min: 1,
        max: 432,
        weight: "6%",
        dbField: "faculty_salaries_rank",
        description: "Ranking based on faculty compensation",
        bigger_is_better: false
      },
      {
        label: "Percent Full-time Faculty",
        value: metrics.percent_full_time_faculty_2,
        min: 12,
        max: 100,
        weight: "2%",
        dbField: "percent_full_time_faculty_2",
        description: "Percentage of faculty employed full-time",
        bigger_is_better: true,
        isPercentage: true
      },
      {
        label: "Student-Faculty Ratio",
        value: metrics.student_faculty_ratio,
        min: 3,
        max: 31,
        weight: "3%",
        dbField: "student_faculty_ratio",
        description: "Ratio of students to faculty members",
        bigger_is_better: false
      }
    ],
    expertOpinion: [
      {
        label: "Peer Assessment Score",
        value: metrics.peer_assessment,
        min: 1.6,
        max: 4.8,
        weight: "20%",
        dbField: "peer_assessment",
        description: "Rating by academic peers",
        bigger_is_better: true
      }
    ],
    financialResources: [
      {
        label: "Financial Resources Rank",
        value: metrics.financial_resources_rank,
        min: 1,
        max: 432,
        weight: "8%",
        dbField: "financial_resources_rank",
        description: "Ranking based on financial resources",
        bigger_is_better: false
      }
    ],
    studentSelectivity: [
      {
        label: "SAT/ACT 25th-75th Percentile",
        value: metrics.sat_act_25th_75th,
        min: 1,
        max: 36,
        weight: "5%",
        dbField: "sat_act_25th_75th",
        description: "Combined SAT/ACT scores",
        bigger_is_better: true
      }
    ],
    facultyResearch: [
      {
        label: "Bibliometric Rank",
        value: metrics.bibliometric_rank,
        min: 1,
        max: 432,
        weight: "4%",
        dbField: "bibliometric_rank",
        description: "Ranking based on research output",
        bigger_is_better: false
      }
    ]
  };
};

/** ------------- Validation and Testing Functions ------------- */
export const validateSimulation = async (universities: Array<{ university: string; metrics: UniversityMetrics; actual_score: number; }>): Promise<ValidationSummary> => {
  // First, get all predictions
  const results = await Promise.all(universities.map(async uni => ({
    university: uni.university,
    actual: uni.actual_score,
    predicted: await calculateSimulatedScore(convertToMetricsState(uni.metrics), undefined, true)
  })));

  const n = results.length;

  // Compute Pearson correlation with denominator check
  const sumX = results.reduce((acc, r) => acc + r.actual, 0);
  const sumY = results.reduce((acc, r) => acc + r.predicted, 0);
  const sumXY = results.reduce((acc, r) => acc + (r.actual * r.predicted), 0);
  const sumXX = results.reduce((acc, r) => acc + (r.actual * r.actual), 0);
  const sumYY = results.reduce((acc, r) => acc + (r.predicted * r.predicted), 0);
  const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));
  const pearsonCorrelation = denominator !== 0 ? (n * sumXY - sumX * sumY) / denominator : 0;

  // Helper function to compute ranks with average ranking for ties
  const rank = (arr: number[]): number[] => {
    const sorted = arr.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value);
    const ranks = new Array(arr.length);
    let i = 0;
    while (i < sorted.length) {
      let j = i;
      while (j < sorted.length && sorted[j].value === sorted[i].value) { j++; }
      const avgRank = (i + 1 + j) / 2;
      for (let k = i; k < j; k++) {
        ranks[sorted[k].index] = avgRank;
      }
      i = j;
    }
    return ranks;
  };

  // Compute Spearman correlation using rank transformation
  const actualValues = results.map(r => r.actual);
  const predictedValues = results.map(r => r.predicted);
  const actualRanks = rank(actualValues);
  const predictedRanks = rank(predictedValues);
  const sumR1 = actualRanks.reduce((acc, r) => acc + r, 0);
  const sumR2 = predictedRanks.reduce((acc, r) => acc + r, 0);
  const sumR1R2 = actualRanks.reduce((acc, r, i) => acc + r * predictedRanks[i], 0);
  const sumR1Sq = actualRanks.reduce((acc, r) => acc + r * r, 0);
  const sumR2Sq = predictedRanks.reduce((acc, r) => acc + r * r, 0);
  const spearmanDenom = Math.sqrt((n * sumR1Sq - sumR1 * sumR1) * (n * sumR2Sq - sumR2 * sumR2));
  const spearmanCorrelation = spearmanDenom !== 0 ? (n * sumR1R2 - sumR1 * sumR2) / spearmanDenom : 0;

  // Calculate error metrics
  const errors = results.map(r => Math.abs(r.predicted - r.actual));
  const meanAbsoluteError = errors.reduce((acc, e) => acc + e, 0) / n;
  const sortedErrors = [...errors].sort((a, b) => a - b);
  const medianAbsoluteError = n % 2 === 1 ? sortedErrors[Math.floor(n / 2)] : (sortedErrors[n/2 - 1] + sortedErrors[n/2]) / 2;
  const rootMeanSquaredError = Math.sqrt(errors.reduce((acc, e) => acc + (e * e), 0) / n);

  // Identify significant outliers (error > 2 standard deviations of the MAE)
  const stdError = Math.sqrt(errors.reduce((acc, e) => acc + Math.pow(e - meanAbsoluteError, 2), 0) / n);
  const outliers = results.filter(r => Math.abs(r.predicted - r.actual) > 2 * stdError)
                          .map(r => ({ university: r.university, predicted: r.predicted, actual: r.actual, error: r.predicted - r.actual }));

  return {
    correlation: pearsonCorrelation,
    meanAbsoluteError,
    medianAbsoluteError,
    rootMeanSquaredError,
    spearmanCorrelation,
    outliers
  };
};

/** ------------- Calibration Optimization ------------- */
interface CalibrationPoint {
  rawScore: number;
  actualScore: number;
}

export const optimizeCalibration = (
  calibrationPoints: CalibrationPoint[]
): void => {
  // Sort points by raw score
  calibrationPoints.sort((a, b) => a.rawScore - b.rawScore);
  
  // Create new score ranges based on actual data points
  const newRanges = [];
  for (let i = 0; i < calibrationPoints.length - 1; i++) {
    const current = calibrationPoints[i];
    const next = calibrationPoints[i + 1];
    
    newRanges.push({
      min: current.rawScore,
      max: next.rawScore,
      baseScore: current.actualScore,
      maxScore: next.actualScore
    });
  }
  
  console.log('Optimized score ranges:', newRanges);
  // Note: You would need to manually update SCORE_RANGES with these values
  // after reviewing them for reasonableness
};

// Function to validate simulator against known scores
export const validateSimulator = async (testCases: ValidationCase[]): Promise<ValidationResult[]> => {
  const results: ValidationResult[] = [];
  
  // Fetch all universities once to use for z-score calculations
  const allUniversities = await fetchAllUniversities();

  for (const testCase of testCases) {
    const predicted = await calculateSimulatedScore(testCase.metrics, allUniversities, true);
    const actual = testCase.score;
    const error = Math.abs(predicted - actual);
    
    results.push({
      university: testCase.university,
      predicted,
      actual,
      error,
    });
  }

  return results;
};

// New function to log statistical analysis after a metric change
export const logStatisticalAnalysis = async (): Promise<void> => {
  try {
    // Fetch the latest university rankings data
    const rankingData = await fetchUniversityRankings();

    // Build test cases from records that have both overall_score and university name
    const testCases = rankingData
      .filter(record => record.overall_score && record.university)
      .map(record => ({
         university: record.university,
         metrics: record,
         actual_score: record.overall_score
      }));

    if (testCases.length === 0) {
      console.log('No valid test cases for statistical analysis.');
      return;
    }

    // Instead of slicing, we now use the full dataset, but temporarily disable logging
    const originalConsoleLog = console.log;
    console.log = () => {};

    // Use the existing validateSimulation to compute correlation and MAE
    const validationResult = await validateSimulation(testCases);

    // Calculate the median difference (median of absolute errors)
    const errors = await Promise.all(testCases.map(async tc => {
       const simulated = await calculateSimulatedScore(convertToMetricsState(tc.metrics), undefined, true);
       return Math.abs(simulated - tc.actual_score);
    }));

    // Restore console.log so that we print only summary logs
    console.log = originalConsoleLog;

    errors.sort((a, b) => a - b);
    const mid = Math.floor(errors.length / 2);
    const medianDifference = errors.length % 2 !== 0 ? errors[mid] : (errors[mid - 1] + errors[mid]) / 2;

    console.log('Pearson correlation:', validationResult.correlation);
    console.log('Mean Absolute Error:', validationResult.meanAbsoluteError);
    console.log('Median Difference:', medianDifference);
  } catch (error) {
    console.error('Error in logStatisticalAnalysis:', error);
  }
};

// Add the following before the if block that attaches logStatisticalAnalysis to the window

declare global {
  interface Window {
    logStatisticalAnalysis: typeof logStatisticalAnalysis;
  }
}

// Expose logStatisticalAnalysis to the global scope if running in a browser
if (typeof window !== 'undefined') {
  window.logStatisticalAnalysis = logStatisticalAnalysis;
}

// ----- New Helper Functions for Custom Calibration Mapping -----

// Custom version of calibrateScore that accepts a candidate officialScores mapping
function calibrateScoreCustom(rawScore: number, candidateMapping: number[]): number {
  // Use the rawSimScores defined above and candidateMapping instead of global officialScores
  const minRaw = rawSimScores[0];
  const maxRaw = rawSimScores[rawSimScores.length - 1];
  const clampedRaw = Math.max(minRaw, Math.min(maxRaw, rawScore));
  
  let calibrated = pchipInterpolate(rawSimScores, candidateMapping, clampedRaw);
  // Round to 1 decimal place and ensure bounds
  calibrated = Math.round(calibrated * 10) / 10;
  return Math.min(100, Math.max(0, calibrated));
}

// Custom simulation function that uses a candidate calibration mapping if provided
export const calculateSimulatedScoreCustom = async (
  metrics: MetricsState,
  candidateMapping?: number[],
  allUniversities?: UniversityMetrics[],
  suppressLogs: boolean = false
): Promise<number> => {
  const universityMetrics = getUniversityMetrics(metrics);
  let rawScore = 0;

  if (!allUniversities) {
    allUniversities = await fetchAllUniversities();
  }

  const realTimeStats: Record<string, { mean: number; std: number; max?: number }> = {};
  for (const metric in METRIC_WEIGHTS) {
    const values = allUniversities.map(u => Number(u[metric]) || 0);
    const filteredValues = values.filter(v => !isNaN(v));
    const mean = filteredValues.reduce((a, b) => a + b, 0) / filteredValues.length;
    const variance = filteredValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / filteredValues.length;
    const std = Math.sqrt(variance) || 1;
    const max = RANK_COLUMNS.has(metric) ? Math.max(...filteredValues) : undefined;
    realTimeStats[metric] = { mean, std, max };
  }

  const BASELINE_METRICS: UniversityMetrics = {
    grad_rate: 0.70,
    retention_rate: 0.85,
    pell_grad_rate: 0.64,
    non_pell_grad_rate: 0.73,
    overperformance_underperformance: 4,
    median_debt: 23168,
    grads_earning_more: 0.85,
    peer_assessment: 2.9,
    faculty_salaries_rank: 195,
    percent_full_time_faculty_2: 0.751,
    student_faculty_ratio: 17,
    financial_resources_rank: 263,
    sat_act_25th_75th: 26,
    bibliometric_rank: 226
  };

  let baselineScore = 0;
  const isBaseline = Object.keys(BASELINE_METRICS).every(metric => {
    const baselineVal = BASELINE_METRICS[metric as keyof typeof BASELINE_METRICS];
    const inputVal = universityMetrics[metric];
    return Math.abs(inputVal - baselineVal) / (Math.abs(baselineVal) || 1) < 0.01;
  });

  if (isBaseline) {
    if (!suppressLogs) console.log('Input metrics match baseline metrics. Returning calibrated score of 55.');
    return 55;
  }

  for (const metric in METRIC_WEIGHTS) {
    const value = Number(universityMetrics[metric]);
    if (isNaN(value)) continue;

    let normalizedValue = value;
    if (PERCENTAGE_FIELDS.has(metric)) {
      normalizedValue = value > 1 ? value / 100.0 : value;
    }

    let z = 0;
    const stats = realTimeStats[metric];
    if (stats && stats.std !== 0) {
      if (RANK_COLUMNS.has(metric) || NEGATIVE_COLUMNS.has(metric)) {
        z = -(normalizedValue - stats.mean) / stats.std;
      } else {
        z = (normalizedValue - stats.mean) / stats.std;
      }
      // Clip z-score to the range [-3, 3] to mitigate extreme values
      z = Math.max(Math.min(z, CLIP_THRESHOLD), -CLIP_THRESHOLD);
      const contribution = METRIC_WEIGHTS[metric] * z;
      rawScore += contribution;
      if (!suppressLogs) {
        console.log(`Metric ${metric}:`, {
          originalValue: value,
          normalizedValue,
          zScore: z,
          weight: METRIC_WEIGHTS[metric],
          contribution
        });
      }
    }
  }

  for (const metric in METRIC_WEIGHTS) {
    let baselineValue = BASELINE_METRICS[metric as keyof UniversityMetrics];
    if (PERCENTAGE_FIELDS.has(metric)) {
      baselineValue = baselineValue > 1 ? baselineValue / 100.0 : baselineValue;
    }

    let baselineZ = 0;
    const stats = realTimeStats[metric];
    if (stats && stats.std !== 0) {
      if (RANK_COLUMNS.has(metric) || NEGATIVE_COLUMNS.has(metric)) {
        baselineZ = -(baselineValue - stats.mean) / stats.std;
      } else {
        baselineZ = (baselineValue - stats.mean) / stats.std;
      }
      baselineScore += METRIC_WEIGHTS[metric] * baselineZ;
    }
  }

  const centeredRawScore = rawScore - baselineScore + 0.171891;
  if (!suppressLogs) console.log('Final raw score:', centeredRawScore);
  let finalScore;
  if (candidateMapping) {
    finalScore = calibrateScoreCustom(centeredRawScore, candidateMapping);
  } else {
    finalScore = calibrateScore(centeredRawScore);
  }
  if (!suppressLogs) console.log('Calibrated score:', finalScore);

  return finalScore;
};

// ----- Optimization Function for Calibration Mapping -----

export const optimizeCalibrationMapping = async (): Promise<void> => {
  try {
    // Fetch data and build test cases
    const rankingData = await fetchUniversityRankings();
    const testCases = rankingData.filter(record => record.overall_score && record.university)
      .map(record => ({
         university: record.university,
         metrics: record,
         actual_score: record.overall_score
      }));
    if (testCases.length === 0) {
      console.log('No valid test cases for optimization.');
      return;
    }

    // Define candidate grid for scale (a) and offset (b) (further refined grid)
    const scaleCandidates: number[] = [];
    for (let a = 0.995; a <= 1.005; a += 0.0005) {
      scaleCandidates.push(parseFloat(a.toFixed(4)));
    }
    const offsetCandidates: number[] = [];
    for (let b = -0.1; b <= 0.1; b += 0.01) {
      offsetCandidates.push(parseFloat(b.toFixed(3)));
    }

    let bestMAE = Infinity;
    let bestA = 1.0;
    let bestB = 0;
    let bestMapping: number[] = [];

    // Loop over candidate parameters
    for (let a of scaleCandidates) {
      for (let b of offsetCandidates) {
        // Create candidate mapping based on the global officialScores
        const candidateMapping = officialScores.map(score => a * score + b);
        let totalError = 0;
        let count = 0;
        for (let tc of testCases) {
          const predicted = await calculateSimulatedScoreCustom(
            convertToMetricsState(tc.metrics),
            candidateMapping,
            undefined,
            true
          );
          totalError += Math.abs(predicted - tc.actual_score);
          count++;
        }
        const mae = totalError / count;
        if (mae < bestMAE) {
          bestMAE = mae;
          bestA = a;
          bestB = b;
          bestMapping = candidateMapping;
        }
      }
    }

    console.log('Optimal calibration mapping found: Scale (a) =', bestA, 'Offset (b) =', bestB);
    console.log('New officialScores mapping:', bestMapping);
    console.log('Optimized Mean Absolute Error:', bestMAE);
    // --- Apply the optimized mapping globally ---
    optimizedMapping = bestMapping;
  } catch (error) {
    console.error('Error optimizing calibration mapping:', error);
  }
};

// ----- Advanced Optimization Function for Calibration Mapping using Simulated Annealing -----
export const optimizeCalibrationMappingAdvanced = async (iterations: number = 1000, initialTemperature: number = 1, coolingRate: number = 0.995): Promise<void> => {
  try {
    // Fetch data and build test cases
    const rankingData = await fetchUniversityRankings();
    const testCases = rankingData.filter(record => record.overall_score && record.university)
      .map(record => ({
         university: record.university,
         metrics: record,
         actual_score: record.overall_score
      }));
    if (testCases.length === 0) {
      console.log('No valid test cases for advanced optimization.');
      return;
    }

    // Helper function to evaluate candidate parameters (a, b) and return MAE
    const evaluateCandidate = async (a: number, b: number): Promise<number> => {
      let totalError = 0;
      for (let tc of testCases) {
        const candidateMapping = officialScores.map(score => a * score + b);
        const predicted = await calculateSimulatedScoreCustom(
          convertToMetricsState(tc.metrics),
          candidateMapping,
          undefined,
          true
        );
        totalError += Math.abs(predicted - tc.actual_score);
      }
      return totalError / testCases.length;
    };

    // Initialize parameters
    let currentA = 1.0, currentB = 0.0;
    let currentMAE = await evaluateCandidate(currentA, currentB);
    let bestA = currentA, bestB = currentB, bestMAE = currentMAE;
    let temperature = initialTemperature;

    for (let i = 0; i < iterations; i++) {
      // Generate candidate perturbations (scale the perturbations with temperature)
      const newA = currentA + (Math.random() - 0.5) * 0.002 * temperature;
      const newB = currentB + (Math.random() - 0.5) * 0.02 * temperature;
      const newMAE = await evaluateCandidate(newA, newB);
      const delta = newMAE - currentMAE;
      // Accept if improvement or with certain probability
      if (delta < 0 || Math.exp(-delta / temperature) > Math.random()) {
        currentA = newA;
        currentB = newB;
        currentMAE = newMAE;
        if (newMAE < bestMAE) {
          bestA = newA;
          bestB = newB;
          bestMAE = newMAE;
        }
      }
      temperature *= coolingRate;
    }

    const bestMapping = officialScores.map(score => bestA * score + bestB);
    optimizedMapping = bestMapping;
    console.log('Advanced Optimization - Best Scale (a):', bestA, 'Best Offset (b):', bestB);
    console.log('Advanced Optimization - Optimized Mean Absolute Error:', bestMAE);
    // Validate with the new mapping
    const validationResult = await validateSimulation(testCases);
    console.log('Advanced Optimization - Validation Pearson Correlation:', validationResult.correlation);
    console.log('Advanced Optimization - Validation Spearman Correlation:', validationResult.spearmanCorrelation);
  } catch (error) {
    console.error('Error in advanced calibration mapping optimization:', error);
  }
};

// ----- Multi-Objective Optimization Function for Calibration Mapping -----
// Objective: minimize MAE + lambda * (max(0, (0.99 - correlation)))^2
export const optimizeCalibrationMappingMultiObjective = async (iterations: number = 2000, initialTemperature: number = 1, coolingRate: number = 0.995, lambda: number = 50): Promise<void> => {
  try {
    // Fetch data and build test cases
    const rankingData = await fetchUniversityRankings();
    const testCases = rankingData.filter(record => record.overall_score && record.university)
      .map(record => ({
         university: record.university,
         metrics: record,
         actual_score: record.overall_score
      }));
    if (testCases.length === 0) {
      console.log('No valid test cases for multi-objective optimization.');
      return;
    }

    // Helper function to evaluate candidate parameters (a, b) in a multi-objective way
    async function evaluateCandidateMulti(a: number, b: number): Promise<{mae: number, correlation: number, objective: number, candidateMapping: number[]}> {
      const candidateMapping = officialScores.map(score => a * score + b);
      let totalError = 0;
      const predictions: number[] = [];
      for (let tc of testCases) {
        const pred = await calculateSimulatedScoreCustom(
          convertToMetricsState(tc.metrics),
          candidateMapping,
          undefined,
          true
        );
        predictions.push(pred);
        totalError += Math.abs(pred - tc.actual_score);
      }
      const mae = totalError / testCases.length;
      // Compute Pearson correlation
      let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0, sumYY = 0;
      const n = testCases.length;
      for (let i = 0; i < n; i++) {
        const x = testCases[i].actual_score;
        const y = predictions[i];
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumXX += x * x;
        sumYY += y * y;
      }
      const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));
      const correlation = denominator !== 0 ? (n * sumXY - sumX * sumY) / denominator : 0;
      // Penalty objective: lower mae and push correlation to at least 0.99
      const penalty = Math.pow(Math.max(0, 0.99 - correlation), 2);
      const objective = mae + lambda * penalty;
      return { mae, correlation, objective, candidateMapping };
    }

    // Initialize parameters
    let currentA = 1.0, currentB = 0.0;
    let currentEval = await evaluateCandidateMulti(currentA, currentB);
    let bestA = currentA, bestB = currentB, bestEval = currentEval;
    let temperature = initialTemperature;

    for (let i = 0; i < iterations; i++) {
      // Generate candidate perturbations scaled by temperature
      const newA = currentA + (Math.random() - 0.5) * 0.002 * temperature;
      const newB = currentB + (Math.random() - 0.5) * 0.02 * temperature;
      const newEval = await evaluateCandidateMulti(newA, newB);
      const delta = newEval.objective - currentEval.objective;
      if (delta < 0 || Math.exp(-delta / temperature) > Math.random()) {
        currentA = newA;
        currentB = newB;
        currentEval = newEval;
        if (newEval.objective < bestEval.objective) {
          bestA = newA;
          bestB = newB;
          bestEval = newEval;
        }
      }
      temperature *= coolingRate;
    }

    optimizedMapping = bestEval.candidateMapping;
    console.log('Multi-Objective Optimization - Best Scale (a):', bestA, 'Best Offset (b):', bestB);
    console.log('Multi-Objective Optimization - MAE:', bestEval.mae, 'Correlation:', bestEval.correlation, 'Objective:', bestEval.objective);
  } catch (error) {
    console.error('Error in multi-objective optimization:', error);
  }
};

declare global {
  interface Window {
    logStatisticalAnalysis: typeof logStatisticalAnalysis;
    optimizeCalibrationMapping: typeof optimizeCalibrationMapping;
    optimizeCalibrationMappingAdvanced: typeof optimizeCalibrationMappingAdvanced;
    optimizeCalibrationMappingMultiObjective: typeof optimizeCalibrationMappingMultiObjective;
  }
}
// Expose optimization function to the global scope if running in a browser
if (typeof window !== 'undefined') {
  window.optimizeCalibrationMapping = optimizeCalibrationMapping;
  window.optimizeCalibrationMappingAdvanced = optimizeCalibrationMappingAdvanced;
  window.optimizeCalibrationMappingMultiObjective = optimizeCalibrationMappingMultiObjective;
}
